import { hellopanda_func as $ } from '../../../assets/script/func.js';

/**
 * SMOOTHWHEEL — 향상된 예외 처리 버전
 * - iframe, 모바일, 블록 내부/상위의 스크롤 가능한 컨테이너를 전부 검사
 * - 런타임에서 휠 이벤트의 타깃이 내부 스크롤 가능한 요소 위면 동작하지 않음
 * - 리사이즈 / DOM 변경 시 재평가
 */
const HELLOPANDA_SMOOTHWHEEL = {
  init() {
    const _ = this;

    // 체크1: iframe 내부면 실행하지 않음
    if (window.self !== window.top) {
      // console.debug('SmoothWheel: running inside iframe — disabled.');
      return;
    }

    // 체크2: 모바일이면 실행하지 않음
    if ($.isDeviceMobile()) {
      // console.debug('SmoothWheel: mobile device — disabled.');
      return;
    }

    document.addEventListener('DOMContentLoaded', () => {
      const blocks = document.querySelectorAll('.wp-block-hellopanda-smoothwheel-block');

      if (!blocks || blocks.length === 0) return;

      // 여러개일 경우 첫번째만 처리 (원하면 확장 가능)
      const block = blocks[0];

      const reason = _.canActivate(block);
      if (reason !== true) {
        // console.warn('SmoothWheel disabled:', reason);
        return;
      }

      _.enable(block);
      _.installObservers();
    });
  },

  /***************
   * 활성화 가능 여부 체크 (전문가 수준)
   * 반환값: true 또는 "비활성화 사유(문자열)"
   ***************/
  canActivate(block) {
    if (!block) return 'no block found';

    // 블록 가시성(숨겨져 있거나 크기가 0이면 비활성)
    const bStyle = getComputedStyle(block);
    if (bStyle.display === 'none' || bStyle.visibility === 'hidden' || (block.offsetWidth === 0 && block.offsetHeight === 0)) {
      return 'block not visible';
    }

    // 상위에 스크롤 가능한 컨테이너가 있는지 검사
    const nearest = this._nearestScrollableAncestor(block);
    const root = document.scrollingElement || document.documentElement;

    // 만약 nearest가 있고 그것이 루트(문서 스크롤링 요소)가 아니면, 블록은 내부 스크롤 컨테이너에 있음
    if (nearest && nearest !== root && nearest !== document.body && nearest !== document.documentElement) {
      return `block is inside a scrollable ancestor (${this._describeElement(nearest)})`;
    }

    // 문서(루트) 자체가 스크롤 가능한지 확인
    // const docScrollable = (root.scrollHeight > root.clientHeight + 1);
    // console.log(root, root.scrollHeight, window.innerHeight, root.clientHeight)
    // if (!docScrollable) {
    //   return 'document is not vertically scrollable (no scrollbar on window)';
    // }

    return true;
  },

  /***************
   * enable / disable
   ***************/
  enable(block) {
    this.block = block;
    this.ease = parseFloat(block.dataset.smoothwheelEase) || 0.1;
    this.tick = parseFloat(block.dataset.smoothwheelTick) || 0.5;
    this.browser = block.dataset.smoothwheelBrowser || null;
    this.disable = block.dataset.smoothwheelDisable || null;
    this.targetScroll = window.scrollY;
    this.currentScroll = window.scrollY;
    this.isScrolling = false;

    // 브라우저 기본 효과
    if (this.browser) {
      document.documentElement.style.scrollBehavior = 'smooth';
      return;
    }

    // 비활성화
    if (this.disable) {
      return;
    }

    function debounce(fn, delay) {
      let timer;
      return function(...args) {
        clearTimeout(timer);
        timer = setTimeout(() => fn.apply(this, args), delay);
      }
    }

    // 바인드된 핸들러를 저장 (나중에 정확히 제거하기 위함)
    this._wheelHandler = this._onWheel.bind(this);
    // this._resizeHandler = this._onResize.bind(this);
    this._resizeHandler = debounce(this._onResize.bind(this), 300);

    window.addEventListener('wheel', this._wheelHandler, { passive: false });
    window.addEventListener('resize', this._resizeHandler);

    // console.info('SmoothWheel enabled');
  },

  disable(reason) {
    if (this._wheelHandler) {
      // removeEventListener의 옵션은 일부 브라우저에서 다르게 동작할 수 있으므로 capture flag(false)로 제거
      window.removeEventListener('wheel', this._wheelHandler, false);
      this._wheelHandler = null;
    }
    if (this._resizeHandler) {
      window.removeEventListener('resize', this._resizeHandler);
      this._resizeHandler = null;
    }
    if (this._mutationObserver) {
      this._mutationObserver.disconnect();
      this._mutationObserver = null;
    }
    // console.info('SmoothWheel disabled', reason || '');
  },

  /***************
   * wheel 이벤트 핸들러 (런타임의 추가 검사 포함)
   ***************/
  _onWheel(e) {
    // 이벤트 타깃이 스크롤 가능한 내부 요소 위에서 발생하면 스크립트는 관여하지 않음
    const nearest = this._nearestScrollableAncestor(e.target);
    const root = document.scrollingElement || document.documentElement;

    if (nearest && nearest !== root && nearest !== document.body && nearest !== document.documentElement) {
      // 내부 스크롤 컨테이너에서 발생한 이벤트이므로 기본 동작 허용
      return;
    }

    // 이제 윈도우 스크롤을 가로채어 부드러운 스크롤 처리
    e.preventDefault();

    this.deltaY = e.deltaY; // 방향 저장

    if (!this.isScrolling) {
      this.currentScroll = window.scrollY;
      this.targetScroll = window.scrollY;
    }
    this.targetScroll += e.deltaY;

    if (!this.isScrolling) {
      this.isScrolling = true;
      requestAnimationFrame(this._smoothScroll.bind(this));
    }
  },

  _smoothScroll() {

    const maxScroll = document.body.scrollHeight - window.innerHeight;
    this.targetScroll = Math.max(0, Math.min(this.targetScroll, maxScroll));

    // 시작, 끝 스크롤 보정
    if (this.targetScroll >= maxScroll && this.deltaY > 0) {
      // console.log('제일 아래로 갈려고 해');
      this.targetScroll += 50;
    } else if (this.targetScroll === 0 && this.deltaY < 0) {
      // console.log('제일 위로 갈려고 해');
      this.targetScroll -= 50;
    }

    this.currentScroll += (this.targetScroll - this.currentScroll) * this.ease;
    window.scrollTo(0, this.currentScroll);

    if (Math.abs(this.targetScroll - this.currentScroll) > this.tick) {
      requestAnimationFrame(this._smoothScroll.bind(this));
    } else {
      this.currentScroll = this.targetScroll;
      // window.scrollTo(0, this.currentScroll); // 강제 보정
      this.isScrolling = false;
    }

  },

  /***************
   * 유틸: 엘리먼트가 스크롤 가능한지 판단
   ***************/
  _isElementScrollable(el) {
    if (!el || el.nodeType !== 1) return false;
    const style = getComputedStyle(el);
    const overflowY = (style.overflowY || '').toLowerCase();
    const isOverflow = /(auto|scroll|overlay)/.test(overflowY);
    const canScroll = el.scrollHeight > el.clientHeight + 1; // 1px 여유
    return isOverflow && canScroll;
  },

  /***************
   * 유틸: 주어진 엘리먼트의 가장 가까운(자신 포함) 스크롤 가능한 상위 엘리먼트 반환
   * 반환: Element 또는 null
   ***************/
  _nearestScrollableAncestor(el) {
    if (!el) return null;
    // 만약 자체가 document.documentElement/body 또는 Text node라면 parent로 올림
    let cur = (el.nodeType === 1) ? el : el.parentElement;
    while (cur && cur !== document.documentElement) {
      if (this._isElementScrollable(cur)) return cur;
      cur = cur.parentElement;
    }
    // documentElement는 보통 루트 스크롤링 요소일 수 있으므로 별도 판별을 위해 null 반환
    return null;
  },

  /***************
   * 리사이즈 시 재평가: 문서가 더 이상 스크롤 가능하지 않거나 블록이 내부 스크롤 컨테이너로 옮겨지면 비활성화
   ***************/
  _onResize() {
    if (!this.block) return;
    const ok = this.canActivate(this.block);
    if (ok !== true) {
      this.disable('re-evaluated on resize: ' + ok);
    }
  },

  /***************
   * DOM 변경(브라우저에서 클래스/노드 추가 등) 감지 후 재평가
   ***************/
  installObservers() {
    if (!('MutationObserver' in window)) return;

    // 디바운스 함수
    const debounce = (fn, delay) => {
      let timer;
      return function(...args) {
        clearTimeout(timer);
        timer = setTimeout(() => fn.apply(this, args), delay);
      };
    };

    // 실제 처리 로직 (디바운스 적용)
    const evaluate = debounce(() => {
      if (!this.block) return;
      const ok = this.canActivate(this.block);
      if (ok !== true) {
        this.disable('re-evaluated by mutation observer: ' + ok);
      }
    }, 100); // 100ms 정도 디바운스, 필요에 따라 조절 가능

    // MutationObserver 생성
    this._mutationObserver = new MutationObserver(evaluate);

    // 필요에 따라 attributes 필터링 가능, 성능 최적화
    this._mutationObserver.observe(document.body, {
      attributes: true, // 속성 변화 감시
      childList: true,  // 자식 노드 추가/삭제 감시
      subtree: true     // 하위 트리 전체 감시
    });
  },

  /***************
   * 디버그용: 간단한 엘리먼트 설명 반환
   ***************/
  _describeElement(el) {
    if (!el) return 'null';
    let desc = el.tagName.toLowerCase();
    if (el.id) desc += `#${el.id}`;
    if (el.classList && el.classList.length) desc += `.${[...el.classList].slice(0,3).join('.')}`;
    return desc;
  }
};

HELLOPANDA_SMOOTHWHEEL.init();